import React from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { FileText, FileSpreadsheet, MessageCircle } from 'lucide-react'
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook'
import { modalActions } from '@common-reducers/ModalReducer'
import { setImportedContactsArray } from '@common-reducers/WhatsAppReducer'
import { updateSearchValue } from '@common-reducers/ContactTableReducer'
import { importItemsActions } from '@common-reducers/ImportItemsReducer'
import { cn } from '@/lib/utils'
import { useTranslation } from 'react-i18next'
import { namespace } from '@common/config/constants'


interface Props {
    /**
     * Where the import was opened from. The New Audience modal passes 'campaign' explicitly: the shared
     * `ModalReducer.openedby` gate is reset by any sibling modal's handleClose (ReusableModal), which made
     * the "WhatsApp Contacts" source vanish for some users (#640). Falls back to the gate when omitted.
     */
    source?: string
}

export default function SelectImportTypeDialog({ source }: Props = {}) {
    const { t } = useTranslation(namespace)
    const gateOpenedby = useAppSelector((state) => state.ModalReducer.openedby)
    const openedby = source ?? gateOpenedby
    const dispatch = useAppDispatch()
    const newAudienceName = useAppSelector((state) => state.ImportItemsReducer.newAudienceName)


    const setImportType = (type: 'csv' | 'google' | 'whatsapp') => {
        if (type === 'whatsapp') {
            dispatch(setImportedContactsArray([]))
        }
        dispatch(modalActions.setImportType(type));
    }

    return (
        <div className="flex flex-col h-full px-8 py-6">
            <div className="w-auto mb-8">
                <h3 className="text-3xl font-semibold tracking-tight text-slate-900 dark:text-slate-50 mb-2">
                    {openedby === 'campaign' ? t('importContacts.title') : t('importContacts.titleBoard')}
                </h3>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                    {openedby === 'campaign' ? t('importContacts.subtitle') : t('importContacts.subtitleBoard')}
                </p>
            </div>

            <div className={cn(
                "flex flex-col justify-center items-center",
                "flex-1"
            )}>
                {newAudienceName !== null && (
                    <div className="mb-8 w-full max-w-md">
                        <Label 
                            htmlFor="audience-name" 
                            className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 block"
                        >
                            {t('importContacts.audienceNameLabel')}
                        </Label>
                        <Input
                            id="audience-name"
                            placeholder={t('importContacts.audienceNamePlaceholder')}
                            onChange={(e) => dispatch(importItemsActions.setNewAudienceName(e.target.value))}
                            value={newAudienceName}
                            className={cn(
                                "h-11",
                                "bg-white dark:bg-slate-800",
                                "text-slate-900 dark:text-slate-50",
                                "border-slate-300 dark:border-slate-600",
                                "focus:border-[var(--color-accent)] dark:focus:border-[var(--color-accent)]",
                                "focus:ring-2 focus:ring-[var(--color-accent)]/20",
                                "transition-all duration-200"
                            )}
                        />
                    </div>
                )}

                <div className="w-full max-w-[280px] space-y-3">
                    <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-4 text-center">
                        {t('importContacts.selectMethodLabel')}
                    </p>
                    
                    <Button
                        className={cn(
                            "w-full h-12 px-4",
                            "bg-[var(--color-accent)] hover:bg-[var(--color-accent)]/90",
                            "text-white dark:text-white",
                            "border-0",
                            "shadow-sm hover:shadow-md",
                            "transition-all duration-200",
                            "hover:scale-[1.01] active:scale-[0.99]",
                            "font-medium text-sm"
                        )}
                        onClick={() => setImportType('csv')}
                    >
                        <div className="w-full flex items-center justify-start gap-2.5">
                            <FileText className="h-4 w-4 flex-shrink-0" />
                            <span className="text-sm">{t('importContacts.methodCsv')}</span>
                        </div>
                    </Button>
                    
                    <Button
                        className={cn(
                            "w-full h-12 px-4",
                            "bg-[var(--color-accent)] hover:bg-[var(--color-accent)]/90",
                            "text-white dark:text-white",
                            "border-0",
                            "shadow-sm hover:shadow-md",
                            "transition-all duration-200",
                            "hover:scale-[1.01] active:scale-[0.99]",
                            "font-medium text-sm"
                        )}
                        onClick={() => setImportType('google')}
                    >
                        <div className="w-full flex items-center justify-start gap-2.5">
                            <FileSpreadsheet className="h-4 w-4 flex-shrink-0" />
                            <span className="text-sm">{t('importContacts.methodGoogleSheets')}</span>
                        </div>
                    </Button>

                    {openedby === 'campaign' && (
                        <Button
                            className={cn(
                                "w-full h-12 px-4",
                                "bg-[var(--color-accent)] hover:bg-[var(--color-accent)]/90",
                                "text-white dark:text-white",
                                "border-0",
                                "shadow-sm hover:shadow-md",
                                "transition-all duration-200",
                                "hover:scale-[1.01] active:scale-[0.99]",
                                "font-medium text-sm"
                            )}
                            onClick={() => {
                                dispatch(updateSearchValue(''))
                                setImportType('whatsapp')
                            }}
                        >
                            <div className="w-full flex items-center justify-start gap-2.5">
                                <MessageCircle className="h-4 w-4 flex-shrink-0" />
                                <span className="text-sm">{t('importContacts.methodWhatsApp')}</span>
                            </div>
                        </Button>
                    )}
                </div>
            </div>
        </div>
    )
}